/**
 * IANA timezone utilities — hardcoded curated list + label formatting.
 *
 * Labels follow the Outlook/Windows convention:
 *   (UTC+HH:MM) Region/City Name
 * e.g. "(UTC+02:00) Europe/Sofia", "(UTC-05:00) America/New York"
 *
 * The `value` of each option is always the raw IANA identifier so the
 * backend can store and interpret it correctly.
 */

/** Curated list of IANA timezone identifiers (Europe-first, then world). */
export const IANA_TIMEZONES: string[] = [
    "UTC",
    "Europe/Sofia",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Rome",
    "Europe/Madrid",
    "Europe/Amsterdam",
    "Europe/Brussels",
    "Europe/Vienna",
    "Europe/Stockholm",
    "Europe/Oslo",
    "Europe/Copenhagen",
    "Europe/Helsinki",
    "Europe/Lisbon",
    "Europe/Warsaw",
    "Europe/Prague",
    "Europe/Budapest",
    "Europe/Bucharest",
    "Europe/Istanbul",
    "Europe/Athens",
    "Europe/Kyiv",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Sao_Paulo",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Australia/Sydney",
    "Pacific/Auckland",
];

/**
 * Compute the current UTC offset for an IANA timezone in minutes.
 * Uses the toLocaleString wall-clock diff trick — no third-party deps.
 */
const getUtcOffsetMinutes = (tz: string): number => {
    const now = new Date();
    const parse = (zone: string) =>
        new Date(now.toLocaleString('en-US', {
            timeZone: zone, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
    return Math.round((parse(tz).getTime() - parse('UTC').getTime()) / 60000);
};

/** Format an offset in minutes as ±HH:MM, e.g. -300 → "-05:00". */
const formatOffsetMinutes = (diffMin: number): string => {
    const sign = diffMin >= 0 ? '+' : '-';
    const absH = Math.floor(Math.abs(diffMin) / 60);
    const absM = Math.abs(diffMin) % 60;
    return `${sign}${String(absH).padStart(2, '0')}:${String(absM).padStart(2, '0')}`;
};

/**
 * Format an IANA identifier into a human-readable Select label.
 * "(UTC+HH:MM) Region/City Name"
 */
export const formatTzLabel = (tz: string): string => {
    const display = tz.replace(/_/g, ' ');
    try {
        return `(UTC${formatOffsetMinutes(getUtcOffsetMinutes(tz))}) ${display}`;
    } catch {
        return display;
    }
};

/** Pre-built antd Select options sorted by UTC offset (most negative → most positive). */
export const TIMEZONE_OPTIONS = IANA_TIMEZONES
    .map(tz => ({label: formatTzLabel(tz), value: tz, _offset: getUtcOffsetMinutes(tz)}))
    .sort((a, b) => a._offset - b._offset)
    .map(({label, value}) => ({label, value}));
